import { defineStore } from "pinia";
import { ref, watch } from "vue";
const ENABLED_KEY = "pa-workspace.enabled";
const LAYOUT_KEY = "pa-workspace.layout.v2";
const LEGACY_LAYOUT_KEY = "pa-workspace.layout";
const VISITS_KEY = "pa-workspace.visits";
const VIEW_KEY = "pa-workspace.view";
const MAX_VISITS = 400;
const DEFAULT_LAYOUT = {
    docSide: "right",
    docWidth: 46,
    docMode: "hidden",
    railOpen: true,
};
function read(storage, key, fallback) {
    try {
        const raw = storage().getItem(key);
        return raw == null ? fallback : JSON.parse(raw);
    }
    catch {
        return fallback;
    }
}
function write(storage, key, value) {
    try {
        storage().setItem(key, JSON.stringify(value));
    }
    catch {
        /* storage is a convenience; the workspace works without it */
    }
}
const local = () => window.localStorage;
/** The saved layout; one carried over from before closed-by-default keeps everything but its open or closed state. */
function readLayout() {
    const saved = read(local, LAYOUT_KEY, null);
    if (saved)
        return { ...DEFAULT_LAYOUT, ...saved };
    const { docMode: _legacyDocMode, ...legacy } = read(local, LEGACY_LAYOUT_KEY, null) ?? {};
    return { ...DEFAULT_LAYOUT, ...legacy };
}
/** PA workspace state: the user's view choice, layout and last visits. */
export const usePaWorkspaceStore = defineStore("pa-workspace", () => {
    const userKey = ref(null);
    const enabled = ref(read(local, ENABLED_KEY, true));
    const ownsFooter = ref(false);
    const layout = ref(readLayout());
    if (layout.value.docMode === "window")
        layout.value.docMode = "docked";
    watch(layout, (value) => write(local, LAYOUT_KEY, { ...value, docMode: value.docMode === "window" ? "docked" : value.docMode }), {
        deep: true,
    });
    function visitsKey(userId) {
        return `${VISITS_KEY}.${userId}`;
    }
    function lastVisit(orderKey, userId) {
        return read(local, visitsKey(userId), {})[orderKey] ?? null;
    }
    function recordVisit(orderKey, userId) {
        const visits = read(local, visitsKey(userId), {});
        const entries = Object.entries({ ...visits, [orderKey]: new Date().toISOString() });
        entries.sort((a, b) => b[1].localeCompare(a[1]));
        write(local, visitsKey(userId), Object.fromEntries(entries.slice(0, MAX_VISITS)));
    }
    /** Load the signed-in user's saved view. */
    function bindUser(userId) {
        if (userId == null || String(userId) === userKey.value)
            return;
        userKey.value = String(userId);
        const saved = read(local, `${VIEW_KEY}.${userKey.value}`, null);
        enabled.value = saved ? saved === "new" : read(local, ENABLED_KEY, true);
    }
    function setView(view) {
        enabled.value = view === "new";
        if (userKey.value)
            write(local, `${VIEW_KEY}.${userKey.value}`, view);
    }
    return {
        enabled,
        ownsFooter,
        layout,
        bindUser,
        setView,
        lastVisit,
        recordVisit,
    };
});
